import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { Compset, Hotelinfo, Ota } from 'frontend-utils/models';

interface HotelDropdownsCompsetSelectionSignature {
  Args: {
    hideCompsetsWithNoData?: boolean;
    selectedOTA: Ota;
    setCompsetId: (compsetId: string) => void;
  };
}

export default class HotelDropdownsCompsetSelection extends Component<HotelDropdownsCompsetSelectionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get allAvailableCompsets() {
    return this.hotelAndMarketService.availableCompsets;
  }

  // Preserve reference for isEqual check in select input
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableCompsetOptions() {
    return this.availableCompsets.map((compset) => ({
      label: this.intl.t(compset.translationKey),
      compset,
    }));
  }

  /**
   * Optional: can determines which compsets we can switch to (if hideCompsetsWithNoData=true is passed)
   *   => optional behaviour is currently used in Rates/Ranking implementations
   *   => default/straightforward behaviour is currently used Rate strategy & Executive dashboard
   *
   * If the own hotel has a matching for the current OTA we will show all available compsets, even if none of the competitors has a matching
   * If it does not, we filter out any compset that has no competitors with a matching for the selected OTA
   * This ensures we always have data to show
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableCompsets() {
    if (this.args.hideCompsetsWithNoData) {
      // If the hotel is matched return all compsets
      if (
        this.hotelAndMarketService.isHotelMatched(this.args.selectedOTA?.id)
      ) {
        return this.allAvailableCompsets;
      }
      // Filter the compsets to return only those that have competitors with matched OTAs
      return this.allAvailableCompsets.filter((compset) => {
        // First we need to create an array that has all ota ids in it.
        // We do this by looping over all competitors of this compset and adding the otas to an array
        // This can be done easily with a reduce
        const compsetOtas = compset.competitors.reduce(
          (otas: (string | null)[], hotelinfo: Hotelinfo) => {
            // create an array with ota ids
            const otaIds = [
              ...otas,
              ...hotelinfo.otas.map((matchedOta) => matchedOta.id),
            ];
            return otaIds;
          },
          [],
        );
        // check if the current selected OTA is matched with any competitor in the compset
        return compsetOtas.includes(this.args.selectedOTA?.id);
      });
    }

    return this.allAvailableCompsets;
  }

  get hasMultipleCompsets(): boolean {
    return this.availableCompsets.length > 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedCompset() {
    return this.availableCompsetOptions.find(
      (option) => option.compset === this.hotelAndMarketService.activeCompset,
    );
  }

  @action
  setCompset(option: Option & { compset: Compset }): void {
    this.hotelAndMarketService.setActiveCompset(option.compset);
    this.args.setCompsetId(option.compset.apiId);
  }

  <template>
    {{#if this.hasMultipleCompsets}}
      <PrismSelectInput
        @onChange={{this.setCompset}}
        @options={{this.availableCompsetOptions}}
        @value={{this.selectedCompset}}
      />
    {{else}}
      <div class="no-filter-content">
        {{this.selectedCompset.label}}
      </div>
    {{/if}}
  </template>
}
